import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { FileCheck2, FilePlus2, Loader2, Search, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal: pick templates → click Generate → show progress → download.
 * Everything happens against /api/documents/leads/{leadId}/*.
 */
export default function GenerateSuiteDialog({ open, onOpenChange, leadId, onGenerated }) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setResults(null);
    api.get(`/documents/leads/${leadId}/generate-preview`)
      .then((r) => {
        setPreview(r.data);
        setSelected(new Set(r.data.suggested_ids));
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Preview failed"))
      .finally(() => setLoading(false));
  }, [open, leadId]);

  const groupedTemplates = useMemo(() => {
    if (!preview) return {};
    const groups = {};
    for (const t of preview.templates) {
      if (q && !t.name.toLowerCase().includes(q.toLowerCase())) continue;
      const g = t.category || "Other";
      groups[g] = groups[g] || [];
      groups[g].push(t);
    }
    return groups;
  }, [preview, q]);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function generate() {
    setRunning(true);
    setResults(null);
    try {
      const r = await api.post(`/documents/leads/${leadId}/generate`, {
        template_ids: Array.from(selected),
      });
      setResults(r.data);
      onGenerated?.();
      toast.success(`${r.data.ok} documents generated${r.data.errors ? `, ${r.data.errors} failed` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    }
    setRunning(false);
  }

  const total = preview?.templates.length || 0;
  const selectedCount = selected.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="generate-suite-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0D1B2A]">Generate document suite</DialogTitle>
          {preview && (
            <p className="text-sm text-slate-500">
              <span className="font-medium text-[#0D1B2A]">{selectedCount}</span> of {total} templates selected
              {" · "}
              <span className="font-medium">{preview.assessment_summary.org_name}</span>
              {" · "}
              <span>{(preview.assessment_summary.audit_type || "").toLowerCase()}</span>
              {!preview.has_logo && (
                <span className="ml-2 text-amber-700 text-xs">
                  <AlertTriangle className="inline w-3 h-3 mr-1" />No logo uploaded — placeholder text will be inserted
                </span>
              )}
            </p>
          )}
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center">Loading templates…</p>
        ) : results ? (
          <ResultsView results={results} />
        ) : (
          <>
            <div className="flex items-center gap-2 pb-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input placeholder="Filter templates..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="suite-search" />
              </div>
              <Button size="sm" variant="outline" onClick={() => setSelected(new Set(preview.suggested_ids))} data-testid="reset-suggested-btn">
                Reset to suggested
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto pr-1 space-y-4 min-h-[300px]">
              {Object.entries(groupedTemplates).sort().map(([g, list]) => (
                <div key={g}>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 sticky top-0 bg-white py-1">{g} · {list.length}</p>
                  <ul className="space-y-1">
                    {list.map((t) => (
                      <li key={t.id}>
                        <label className={`flex items-start gap-3 p-2 border rounded cursor-pointer transition-colors ${
                          selected.has(t.id) ? "bg-[#E6F4F1]/50 border-[#1A7F6E]/40" : "border-slate-200 hover:border-slate-300"
                        }`} data-testid={`tpl-item-${t.id}`}>
                          <Checkbox checked={selected.has(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`tpl-check-${t.id}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#0D1B2A] truncate">{t.name}</p>
                            <p className="text-[11px] text-slate-500">
                              {(t.audit_types || []).join("/")} · Modules: {(t.modules || []).join(", ")}
                              {t.placeholders_known?.length ? ` · ${t.placeholders_known.length} placeholder(s)` : ""}
                            </p>
                          </div>
                          {preview.suggested_ids.includes(t.id) && (
                            <span className="text-[10px] font-semibold text-[#1A7F6E] bg-[#E6F4F1] px-1.5 py-0.5 rounded">SUGGESTED</span>
                          )}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <DialogFooter className="pt-3 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={generate}
                disabled={running || selectedCount === 0}
                className="bg-[#1A7F6E] hover:bg-[#136053] text-white"
                data-testid="run-generation-btn"
              >
                {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating {selectedCount}…</> : <><FilePlus2 className="w-4 h-4 mr-2" />Generate {selectedCount} document{selectedCount === 1 ? "" : "s"}</>}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResultsView({ results }) {
  return (
    <div className="flex-1 overflow-y-auto pr-1" data-testid="generation-results">
      <div className="mb-3 p-4 bg-[#E6F4F1] rounded-md flex items-center gap-3">
        <CheckCircle2 className="w-8 h-8 text-[#1A7F6E]" />
        <div>
          <p className="text-lg font-semibold text-[#0D1B2A]">{results.ok} document{results.ok === 1 ? "" : "s"} generated</p>
          <p className="text-xs text-slate-600">
            {results.errors > 0 && <span className="text-rose-700">{results.errors} failed. </span>}
            {results.logo_used ? "Client logo inserted." : "No client logo uploaded — placeholder text used."}
          </p>
        </div>
      </div>
      <ul className="space-y-1">
        {results.results.map((r) => (
          <li key={r.template_id} className={`flex items-start gap-2 text-sm p-2 rounded ${r.status === "ok" ? "text-[#0D1B2A]" : "text-rose-700 bg-rose-50"}`}>
            {r.status === "ok"
              ? <FileCheck2 className="w-4 h-4 text-[#1A7F6E] mt-0.5 shrink-0" />
              : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="truncate">{r.document?.template_name || r.template_id}</p>
              {r.error && <p className="text-xs text-rose-700">{r.error}</p>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
